/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const privacyPolicyUrl = `static/privacy-policy`;
export const privacyPolicyUrlPreLogin = '/privacy-policy';

const PrivacyPolicy: React.FC = () => {
  const { i18n } = useTranslation();
  const lang = useMemo(() => i18n.language, [i18n.language]);
  const renderSwitch = (lng: string) => {
    switch (lng) {
      case 'en':
        return <PrivacyPolicyEnglish />;
      case 'fi':
        return <PrivacyPolicyFinnish />;
      case 'sv':
        return <PrivacyPolicySwedish />;
      default:
        return <PrivacyPolicyFinnish />;
    }
  };

  return <div className="finbb-container">{renderSwitch(lang)}</div>;
};

const PrivacyPolicyEnglish: React.FC = () => {
  return (
    <>
      <h1 className="static-h1">MyBiobank Privacy Statement</h1>
      <p>
        <i>23.5.2024</i>
      </p>
      <p>
        We are committed to respecting and safeguarding the privacy of the users
        of the MyBiobank service. The MyBiobank service is a biobank service for
        users who have given their biobank consent so that they can choose to
        participate electronically in different research projects, and for
        researchers for collecting research data. The service is provided by the
        Finnish Biobank Cooperative – FINBB in collaboration with biobanks.
      </p>
      <h4>Controller</h4>
      <p>
        The controller of personal data files is the biobank organisation, or
        biobank organisations, for which the user has given a biobank consent,
        which has invited the user to participate in the service, or for which
        the user gives their consent and contact details for the purpose of
        using e-services through the service. The biobank organisations and
        links to their homepages can be found from{' '}
        <a href="https://finbb.fi/fi/member-biobanks">
          https://finbb.fi/fi/member-biobanks
        </a>
        . The biobanks’ privacy statements are on their homepages.
      </p>
      <p>
        If a sample donor participates in a research project through the
        service, the controller of the research data will be separately
        indicated in the service prior to the participation.
      </p>
      <p>
        With regard to measures to ensure the usability of the MyBiobank
        service, the controller of personal data file is the Finnish Biobank
        Cooperative – FINBB, c/o Regus, Yliopistonkatu 31, FI- 20100 Turku,
        Finland, business ID 2853268-2. FINBB’s data protection officer can be
        reached by e- mail at{' '}
        <a href="mailto:tietosuojavastaava@finbb.fi">
          tietosuojavastaava@finbb.fi
        </a>
        .
      </p>
      <h4>Reasons for processing personal data</h4>
      <p>
        The reasons for processing personal data are indicated in the biobanks’
        privacy statements, which can be found from the biobanks’ homepages, and
        in the information on a research project that is presented prior to any
        participation in the research project.
      </p>
      <p>
        The Finnish Biobank Cooperative – FINBB may also process personal data
        to ensure the functionality of the service, for example, to prevent
        misuse or to investigate cases of suspected misuse, and to respond to
        potential claims. In this regard, the right to process personal data is
        based on Article 6(1)(f) of the EU’s General Data Protection Regulation:
        processing is necessary for the purposes of the legitimate interests
        pursued by the controller or by a third party.
      </p>
      <h4>Collection of personal data</h4>
      <p>
        Information collected on the user following registration with the
        service:
      </p>
      <ul>
        <li>
          In connection with the user’s authentication, their personal identity
          code is pseudonymized: the identity code is replaced with a secure
          code, which cannot be directly linked with the original code. This
          pseudocode will then be used in the research project; otherwise, no
          personal identity codes or other identification data will be processed
          in the system
        </li>
        <li>E-mail address, optional</li>
        <li>Telephone number, optional</li>
        <li>Consent to electronic services, optional</li>
        <li>Consent to participate in the research project, optional</li>
        <li>
          Consent to inform health care of research findings relevant to the
          participant’s health, optional
        </li>
        Pseudonymised questionnaire responses of those who gave their consent
        to, and participated in, the research project will be stored directly in
        the systems used by the research project, to which the participants have
        transferred via links form MyBiobank. This information will not be
        stored in MyBiobank.
      </ul>
      <h4>Disclosure of personal data</h4>
      <p>
        No personal data collected in the service is regularly disclosed to
        outside parties. The data collected in the service may be transferred or
        copied to another environment; information on the consent to electronic
        services, for example, as well as contact details will be stored in the
        consent register of the biobank organisation.
      </p>
      <p>
        As to the data processing carried out in biobanks, information on
        potential disclosure is provided in the biobank’s privacy statement.
      </p>
      <p>
        Any regular disclosures of research data are indicated in the
        information concerning the research project prior to any participation
        in the research project.
      </p>
      <h4>Retention of personal data</h4>
      <p>
        We will retain your personal data for as long as they are needed for the
        purpose they were collected. The necessity of the data will be assessed
        on a regular basis.
      </p>
      <p>
        Authentication with the MyBiobank service is necessary to be able to use
        the service. Authentication takes place via the Suomi.fi service. The
        data in the register of the Suomi.fi online service will be retained for
        the duration of the authentication session only. For further information
        about the processing of personal data in the suomi.fi service can be
        found at www.suomi.fi.
      </p>
      <h4>Rights of the data subject</h4>
      <p>
        A data subject – an individual whose personal data we process – has the
        right, as described in greater detail in the EU’s General Data
        Protection Regulation and in the Finnish Data Protection Act, to:
      </p>
      <ul>
        <li>be informed about the processing of their personal data;</li>
        <li>gain access to their personal data;</li>
        <li>have their personal data rectified;</li>
        <li>have their personal data erased;</li>
        <li>restrict the processing of their data;</li>
        <li>object to the processing of their data;</li>
        <li>have their data transferred to another system.</li>
      </ul>
      <p>
        Each data subject also has the right to file a complaint with a
        supervisory authority: The Office of the Data Protection Ombudsman,
        Ratapihantie 9, 6th floor, FI-00520 Helsinki, Finland, tietosuoja@om.fi.
      </p>
      <p>
        The provision of personal data is a necessary prerequisite for the use
        of the system.
      </p>
      <h4>Service provider</h4>
      <div className="static-block">
        <div>
          <strong className="static-service-detail">Name:</strong>Finnish
          Biobank Cooperative – FINBB
        </div>
        <div>
          <strong className="static-service-detail">Registered office:</strong>
          Turku, Finland
        </div>
        <div>
          <strong className="static-service-detail">Address:</strong>c/o Regus,
          Yliopistonkatu 31, FI-20100 Turku, Finland
        </div>
        <div>
          <strong className="static-service-detail">Business ID:</strong>
          2853268-2
        </div>
      </div>
      <div className="static-block">
        <div className="static-container">
          <div>Auria Biobank</div>
          <a href="https://www.auria.fi/biopankki/index.php">
            www.auria.fi/biopankki/index.php
          </a>
        </div>
        <div className="static-container">
          <div>Helsinki Biobank</div>
          <a href="https://www.helsinginbiopankki.fi">
            www.helsinginbiopankki.fi
          </a>
        </div>
        <div className="static-container">
          <div>Finnish Clinical Biobank Tampere</div>
          <a href="https://www.tays.fi/biopankki">www.tays.fi/biopankki</a>
        </div>
      </div>
    </>
  );
};

const PrivacyPolicyFinnish: React.FC = () => {
  return (
    <>
      <h1 className="static-h1">OmaBiopankki Tietosuojaseloste</h1>
      <p>
        <i>22.5.2024</i>
      </p>
      <p>
        Olemme sitoutuneet kunnioittamaan ja turvaamaan Omabiopankki-palvelun
        käyttäjien yksityisyyttä. Omabiopankki-palvelu on biopankkien palvelu
        biopankkisuostumuksen antaneille käyttäjille, jotta he voivat
        halutessaan osallistua sähköisesti eri tutkimushankkeisiin ja
        tutkijoille tutkimustietojen keräämiseksi. Palvelun toteuttaa
        Biopankkien Osuuskunta Suomi – FINBB yhteistyössä biopankkien kanssa.
      </p>
      <h4>Rekisterinpitäjä</h4>
      <p>
        Henkilötietojen rekisterinpitäjä on se biopankkiorganisaatio tai ne
        biopankkiorganisaatiot, johon/joihin käyttäjä on antanut
        biopankkisuostumuksen, joka on kutsunut käyttäjän osallistumaan
        palveluun, tai jolle/joille käyttäjä antaa suostumuksen ja yhteystiedot
        sähköiseen asiointiin palvelun kautta. Biopankkiorganisaatiot ja linkit
        niiden kotisivuille löytyvät osoitteesta{' '}
        <a href="https://finbb.fi/fi/member-biobanks">
          https://finbb.fi/fi/member-biobanks
        </a>
        . Kunkin biopankin omat tietosuojailmoitukset löytyvät niiden
        kotisivuilta.
      </p>
      <p>
        Mikäli näytteenantaja osallistuu palvelun kautta tutkimushankkeeseen,
        tutkimusaineiston rekisterinpitäjä ilmoitetaan palvelussa erikseen ennen
        tutkimukseen osallistumista.
      </p>
      <p>
        OmaBiopankki-palvelun käytettävyyden varmistamiseen liittyvien
        toimenpiteiden osalta henkilötietojen rekisterinpitäjänä on Biopankkien
        Osuuskunta Suomi – FINBB, c/o Regus, Yliopistonkatu 31, 20100 Turku,
        y-tunnus 2853268-2. FINBB:n tietosuojavastaavan tavoittaa sähköpostitse
        osoitteesta{' '}
        <a href="mailto:tietosuojavastaava@finbb.fi">
          tietosuojavastaava@finbb.fi
        </a>
        .
      </p>
      <h4>Käsittelyperusteet</h4>
      <p>
        Käsittelyperusteet ilmenevät biopankkien tietosuojaselosteista, jotka
        löytyvät biopankkien kotisivuilta, ja tutkimushankkeiden tiedoista,
        jotka esitetään ennen tutkimukseen osallistumista.
      </p>
      <p>
        Biopankkien Osuuskunta Suomi – FINBB voi käsitellä henkilötietoja myös
        palvelun toimivuuden varmistamiseksi, kuten esimerkiksi väärinkäytön
        estämiseksi tai väärinkäyttöepäilyjen tutkimiseksi, sekä mahdollisiin
        vaateisiin vastaamiseksi. Näiltä osin oikeus henkilötietojen käsittelyyn
        perustuu EU:n yleisen tietosuoja-asetuksen 6 artiklan 1 kohdan f
        alakohtaan: käsittely on tarpeen rekisterinpitäjän tai kolmannen
        osapuolen oikeutettujen etujen toteuttamiseksi.
      </p>
      <h4>Henkilötietojen kerääminen</h4>
      <p>
        Palveluun rekisteröitymisen jälkeen käyttäjästä kerättävät
        henkilötiedot:
      </p>
      <ul>
        <li>
          Käyttäjän tunnistautumisen yhteydessä hänen henkilötunnuksensa
          pseudonymisoidaan, eli siitä muodostetaan tietoturvallisesti koodi,
          jota ei enää voi suoraan yhdistää henkilötunnukseen, ja jota käytetään
          tutkimuksessa henkilötietojen suojaamiseksi; muutoin järjestelmässä ei
          käsitellä henkilötunnuksia tai muita tunnistetietoja
        </li>
        <li>Sähköpostiosoite, vapaaehtoinen</li>
        <li>Puhelinnumero, vapaaehtoinen</li>
        <li>Sähköisen asioinnin suostumus, vapaaehtoinen</li>
        <li>Suostumus tutkimushankkeeseen osallistumiseen, vapaaehtoinen</li>
        <li>
          Hyväksyntä biopankille viedä tutkimukseen osallistuneen
          tutkimustietoja terveydenhuoltoon, vapaaehtoinen
        </li>
      </ul>
      <p>
        Tutkimushankkeeseen suostumuksensa antaneen ja siihen osallistuva
        henkilön pseudonymisoidut kyselytutkimusvastaukset tallentuvat suoraan
        kunkin tutkimushankkeen käyttämään järjestelmään, johon käyttäjä on
        siirtynyt OmaBiopankista linkin kautta. Nämä tiedot eivät tallennu
        OmaBiopankkiin.
      </p>
      <h4>Henkilötietojen luovutus</h4>
      <p>
        Palvelussa kerättäviä henkilötietoja ei säännönmukaisesti luovuteta
        ulkopuolisille. Palvelussa kerättävä tieto voidaan siirtää tai kopioida
        toiseen ympäristöön, esimerkiksi tiedot sähköisen asioinnin
        suostumuksesta ja yhteystiedoista tallennetaan biopankkiorganisaation
        suostumusrekisteriin.
      </p>
      <p>
        Biopankeissa tapahtuvan tietojenkäsittelyn osalta tieto mahdollisista
        luovutuksista on biopankin tietosuojaselosteessa.
      </p>
      <p>
        Tutkimusaineiston mahdollisista säännönmukaisista luovutuksista
        ilmoitetaan tutkimushankkeen tiedoissa ennen tutkimukseen
        osallistumista.
      </p>
      <h4>Henkilötietojen säilytys</h4>
      <p>
        Säilytämme tietojasi niin kauan kuin niitä tarvitaan siihen
        tarkoitukseen, jota varten ne on kerätty. Tietojen tarpeellisuus
        arvioidaan säännöllisesti.
      </p>
      <p>
        Omabiopankki-palveluun tunnistautuminen on sen käytön kannalta
        välttämätöntä. Tunnistautuminen tehdään Suomi.fi-palvelun kautta.
        Suomi.fi-verkkopalvelun rekisterin tietoja säilytetään vain
        tunnistautumisistunnon ajan. Lisätietoja suomi.fi henkilötietojen
        käsittelystä löytyy osoitteesta www.suomi.fi.
      </p>
      <h4>Rekisteröidyn oikeudet</h4>
      <p>
        Rekisteröidyllä, eli henkilöllä, jonka henkilötietoja käsittelemme, on
        EU:n yleisessä tietosuoja-asetuksessa ja Suomen tietosuojalaissa
        tarkemmin kuvatusti oikeus:
      </p>
      <ul>
        <li>saada tietoa henkilötietojensa käsittelystä</li>
        <li>saada pääsy henkilötietoihin</li>
        <li>tietojen oikaisemiseen</li>
        <li>tietojen poistamiseen</li>
        <li>tietojen käsittelyn rajoittamiseen</li>
        <li>vastustaa tietojen käsittelyä</li>
        <li>siirtää tiedot järjestelmästä toiseen.</li>
      </ul>
      <p>
        Jokaisella rekisteröidyllä on myös oikeus tehdä valitus
        valvontaviranomaiselle: Tietosuojavaltuutetun toimisto, Ratapihantie 9,
        6.krs, 00520 Helsinki, tietosuoja@om.fi.
      </p>
      <p>Henkilötietojen antaminen on järjestelmän käytön edellytys.</p>
      <h4>Palvelun tarjoaja</h4>
      <div className="static-block">
        <div>
          <strong className="static-service-detail">Nimi:</strong>FINBB –
          Biopankkien Osuuskunta Suomi
        </div>
        <div>
          <strong className="static-service-detail">Kotipaikka:</strong>
          Turku
        </div>
        <div>
          <strong className="static-service-detail">Osoite:</strong>c/o Regus,
          Yliopistonkatu 31, 20100, Turku, Finland
        </div>
        <div>
          <strong className="static-service-detail">Y-tunnus:</strong>
          2853268-2
        </div>
      </div>
      <div className="static-block">
        <div className="static-container">
          <div>Auria Biobankki</div>
          <div>
            <a href="https://www.auria.fi/biopankki/index.php">
              www.auria.fi/biopankki/index.php
            </a>
            <span> &#8594; Tietosuojaseloste</span>
          </div>
        </div>
        <div className="static-container">
          <div>Helsingin Biobankki</div>
          <div>
            <a href="https://www.helsinginbiopankki.fi">
              www.helsinginbiopankki.fi
            </a>
            <span> &#8594; Kansalaiselle &#8594; Tietosuojakäytänteet</span>
          </div>
        </div>
        <div className="static-container">
          <div>Tampereen Biopankki</div>
          <div>
            <a href="https://www.tays.fi/biopankki">www.tays.fi/biopankki</a>
            <span> &#8594; Tietosuoja</span>
          </div>
        </div>
      </div>
    </>
  );
};

const PrivacyPolicySwedish: React.FC = () => {
  return (
    <>
      <h1 className="static-h1">Dataskyddsbeskrivning för MinBiobank</h1>
      <p>
        <i>23.5.2024</i>
      </p>
      <p>
        Vi har förbundit oss att respektera och trygga MinBiobank-tjänstens
        användares integritet. Tjänsten MinBiobank är biobankernas tjänst för
        användare som gett sitt biobankssamtycke för att om de vill kunna delta
        elektroniskt i olika forskningsprojekt och samla in forskningsdata till
        forskare. Tjänsten tillhandahålls av Biopankkien Osuuskunta Suomi –
        FINBB i samarbete med biobankerna.
      </p>
      <h4>Registeransvarig</h4>
      <p>
        Registeransvarig för personuppgifterna är den biobanksorganisation eller
        de biobanksorganisationer till vilken/vilka användaren har gett sitt
        biobankssamtycke, som har bjudit in användaren att delta i tjänsten,
        eller till vilken/vilka användaren ger sitt samtycke och sina
        kontaktuppgifter för elektronisk ärendehantering via tjänsten.
        Biobanksorganisationerna och länkarna till deras hemsidor kan finnas
        från{' '}
        <a href="https://finbb.fi/fi/member-biobanks">
          https://finbb.fi/fi/member-biobanks
        </a>
        . Biobankernas dataskyddsbeskrivningar fanns på deras hemsidor.
      </p>
      <p>
        Om en provgivare deltar i ett forskningsprojekt via tjänsten, informeras
        den som är registeransvarig för forskningsmaterialet separat i tjänsten
        innan studiedeltagandet inleds.
      </p>
      <p>
        När det gäller åtgärder med avseende på att säkerställa
        MinBiobank-tjänstens tillgänglighet är Biopankkien Osuuskunta Suomi –
        FINBB, c/o Regus, Universitetsgatan 31, 20100 Åbo, FO-nummer 2853268-2,
        registeransvarig för personuppgifterna. FINBB datasekretessansvarig kan
        kontaktas per e-post på adressen{' '}
        <a href="mailto:tietosuojavastaava@finbb.fi">
          tietosuojavastaava@finbb.fi
        </a>
        .
      </p>
      <h4>Behandling av personuppgifter</h4>
      <p>
        Grunderna för behandlingen av personuppgifter framgår av biobankernas
        hemsidor och av forskningsprojektens uppgifter som presenteras före
        studiedeltagandet.
      </p>
      <p>
        Biopankkien Osuuskunta Suomi – FINBB kan behandla personuppgifter även
        för att säkerställa att tjänsten fungerar, till exempel för att
        förhindra missbruk eller för att undersöka misstankar om missbruk, samt
        för att efterkomma krav. Till dessa delar grundar sig rätten att
        behandla personuppgifter på punkt 1 f i artikel 6 i EU:s allmänna
        dataskyddsförordning: behandlingen är nödvändig för ändamål som rör den
        personuppgiftsansvariges eller en tredje parts berättigade intressen.
      </p>
      <h4>Insamling av personuppgifter</h4>
      <p>
        Följande personuppgifter samlas in om användaren efter registreringen i
        tjänsten:
      </p>
      <ul>
        <li>
          I samband med identifieringen av användaren pseudonymiseras deras
          personbeteckning, det vil säga en kod som inte länge direkt kan
          kopplas till personbeteckningen skapas av den på ett
          informationssäkert sätt. Denna kod skall sedan användas i studien. I
          övrigt behandlas personbeteckningar eller andra
          identifikationsuppgifter inte i systemet
        </li>
        <li>E-postadress, frivilligt</li>
        <li>Telefonnummer, frivilligt</li>
        <li>Samtycke till elektronisk ärendehantering, frivilligt</li>
        <li>Samtycke till deltagande i forskningsprojekt, frivilligt</li>
        <li>
          Samtycke för biobanken att exportera forskningsdeltagarens
          forskningsdata till hälsovården, frivilligt
        </li>
      </ul>
      <p>
        Pseudonymiserade enkätundersökningssvar för personer som gett sitt
        samtycke till och deltagit i forskningsprojekt lagras direkt i det
        system som används av varje forskningsprojekt, dit användaren har
        flyttat från MinBiobank via en länk. Denna information lagras inte i
        MinBiobank.
      </p>
      <h4>Överlämnande av personuppgifter</h4>
      <p>
        De personuppgifter som samlas in i tjänsten utlämnas regelmässigt inte
        till utomstående. Data som samlas in i tjänsten kan flyttas eller
        kopieras till en annan miljö, till exempel sparas uppgifter om samtycke
        till elektronisk ärendehantering och kontaktuppgifter i
        biobankorganisationens samtyckesregister.
      </p>
      <p>
        För den databehandling som sker i biobankerna ingår uppgifter om
        eventuella överlämnanden i biobankens dataskyddsbeskrivning.
      </p>
      <p>
        Om eventuellt regelmässigt utlämnande av forskningsmaterial meddelas i
        forskningsprojektets uppgifter före studiedeltagandet..
      </p>
      <h4>Förvaring av personuppgifter</h4>
      <p>
        Vi sparar dina uppgifter så länge som de behövs för det ändamål för
        vilket de har samlats in. Uppgifternas nödvändighet bedöms regelbundet.
      </p>
      <p>
        Identifieringen i MinBiobank-tjänsten är avgörande för dess användning.
        Identifieringen görs via Suomi.fi-tjänsten. Uppgifterna i webbtjänsten
        Suomi.fi’s register sparas endast under identifieringssessionen. Mer
        information om behandlingen av personuppgifter i Suomi.fi finns på
        adressen www.suomi.fi.
      </p>
      <h4>Den registrerades rättigheter</h4>
      <p>
        Den registrerade, dvs. personen vars personuppgifter vi behandlar, har
        enligt EU:s allmänna dataskyddsförordning och Finlands dataskyddslag
        rätt:
      </p>
      <ul>
        <li>att få information om hur personuppgifterna behandlas</li>
        <li>att få tillgång till personuppgifterna</li>
        <li>till rättelse av uppgifter</li>
        <li>till radering av uppgifter</li>
        <li>till begränsning av behandling</li>
        <li>att motsätta sig behandling av uppgifter</li>
        <li>till dataportabilitet.</li>
      </ul>
      <p>
        Varje registrerad har även rätt att anföra besvär hos
        tillsynsmyndigheten: Dataombudsmannens byrå, Bangårdsvägen 9, vån. 6,
        00520 Helsingfors, tietosuoja@om.fi.
      </p>
      <p>
        Att uppge personuppgifter är en förutsättning för att kunna använda
        tjänsten.
      </p>
      <h4>Tjänsteleverantör</h4>
      <div className="static-block">
        <div>
          <strong className="static-service-detail">Namn:</strong>FINBB –
          Biopankkien Osuuskunta Suomi
        </div>
        <div>
          <strong className="static-service-detail">Hemort:</strong>
          Åbo
        </div>
        <div>
          <strong className="static-service-detail">Adress:</strong>c/o Regus,
          Universitetsgatan 31, 20100, Åbo, Finland
        </div>
        <div>
          <strong className="static-service-detail">FO-nummer:</strong>
          2853268-2
        </div>
      </div>
      <div className="static-block">
        <div className="static-container">
          <div>Auria Biobank</div>
          <a href="https://www.auria.fi/biopankki/index.php">
            www.auria.fi/biopankki/index.php
          </a>
        </div>
        <div className="static-container">
          <div>Helsingfors Biobank</div>
          <a href="https://www.helsinginbiopankki.fi">
            www.helsinginbiopankki.fi
          </a>
        </div>
        <div className="static-container">
          <div>Tammerfors Biobank</div>
          <a href="https://www.tays.fi/biopankki">www.tays.fi/biopankki</a>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
